'use client';

import Link from 'next/link';
import type { ActivePackage } from '@/types/package';
import {
  BASIC_INCLUDED_FEATURES,
  buildCardFeatures,
  formatInrPrice,
  getPackageBadge,
  getPackageSavings,
  getPaidPlanTheme,
} from '@/utils/membershipUtils';
import DesktopMembershipComparison from './DesktopMembershipComparison';
import { MembershipPricingSkeleton } from './MembershipSkeleton';
import { Check, Crown, X } from 'lucide-react';

type MembershipPricingSectionProps = {
  packages: ActivePackage[];
  isLoading: boolean;
  loadError: string | null;
  isAuthenticated: boolean;
  onSelectPackage: (packageId: string) => void;
};

function PaidPlanCrownIcon({ className }: { className?: string }) {
  return <Crown className={className} strokeWidth={1.75} />;
}

function PaidPlanFeatureList({
  pkg,
  isAuthenticated,
}: {
  pkg: ActivePackage;
  isAuthenticated: boolean;
}) {
  const features = buildCardFeatures(pkg);

  return (
    <div className="mb-8 text-left">
      <h4 className="mb-4 font-semibold text-gray-900">All Free Features +</h4>
      <ul className="space-y-3">
        {features.map((feature) => (
          <li key={feature.name} className="flex items-center gap-3">
            {feature.included ? (
              <Check className="h-5 w-5 flex-shrink-0 text-green-600" />
            ) : (
              <X className="h-5 w-5 flex-shrink-0 text-red-600" />
            )}
            <span className={feature.included ? 'text-gray-700' : 'text-gray-500 line-through'}>
              {feature.name}
              {feature.included && feature.value && isAuthenticated
                ? ` (${feature.value})`
                : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MembershipPricingSection({
  packages,
  isLoading,
  loadError,
  isAuthenticated,
  onSelectPackage,
}: MembershipPricingSectionProps) {
  const theme = getPaidPlanTheme();

  if (isLoading) {
    return <MembershipPricingSkeleton />;
  }

  return (
    <>
      {loadError && (
        <div className="mb-6 rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          {loadError}
        </div>
      )}

      {packages.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-600">
          Membership plans are temporarily unavailable. Please try again later or contact support.
        </div>
      ) : (
        <>
          <DesktopMembershipComparison
            packages={packages}
            isAuthenticated={isAuthenticated}
            onSelectPackage={onSelectPackage}
          />

          <div className="mx-auto max-w-6xl md:hidden">
            <div className="grid grid-cols-1 gap-8">
              <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-lg">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gray-300">
                  <Check className="h-8 w-8 text-gray-600" />
                </div>
                <h3 className="mb-4 text-2xl font-bold text-gray-600">Free Plan</h3>
                <div className="mb-4 inline-block rounded-full bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700">
                  30 Days Trial
                </div>
                <div className="mb-2 text-4xl font-bold text-gray-600">₹0</div>
                <div className="mb-6 text-sm text-gray-500">No hidden charges</div>
                <div className="mb-8 text-left">
                  <h4 className="mb-4 font-semibold text-gray-900">What&apos;s Included:</h4>
                  <ul className="space-y-3">
                    {BASIC_INCLUDED_FEATURES.map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <Check className="h-5 w-5 flex-shrink-0 text-green-600" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  type="button"
                  disabled
                  className="w-full rounded-xl bg-gray-600 py-3 px-6 font-semibold text-white shadow-lg"
                >
                  Current Plan
                </button>
              </div>

              {packages.map((pkg) => {
                const savings = getPackageSavings(pkg);
                const badge = getPackageBadge(pkg);

                return (
                  <div
                    key={pkg.id}
                    className={`relative rounded-2xl border bg-white p-8 text-center shadow-lg ${theme.borderClass}`}
                  >
                    {badge && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 transform">
                        <div
                          className={`rounded-full px-4 py-2 text-sm font-bold shadow-lg ${badge.className}`}
                        >
                          {badge.label}
                        </div>
                      </div>
                    )}

                    <div
                      className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full ${theme.iconBgClass}`}
                    >
                      <PaidPlanCrownIcon className={`h-8 w-8 ${theme.iconClass}`} />
                    </div>

                    <h3 className={`mb-4 text-2xl font-bold ${theme.titleClass}`}>{pkg.name}</h3>
                    <div
                      className={`mb-4 inline-block rounded-full px-4 py-2 text-sm font-semibold ${theme.pillClass}`}
                    >
                      {pkg.durationMonths} months
                    </div>
                    <div className={`mb-2 text-4xl font-bold ${theme.priceClass}`}>
                      {formatInrPrice(pkg.offerPrice)}
                    </div>
                    {savings && (
                      <>
                        <div className="mb-2 text-sm text-gray-500 line-through">
                          {formatInrPrice(pkg.price)}
                        </div>
                        <div className="mb-6 inline-block rounded-full bg-green-100 px-4 py-2 text-sm font-semibold text-green-700">
                          Save {formatInrPrice(savings.amount)} ({savings.percent}% OFF)
                        </div>
                      </>
                    )}

                    <PaidPlanFeatureList pkg={pkg} isAuthenticated={isAuthenticated} />

                    {isAuthenticated ? (
                      <button
                        type="button"
                        onClick={() => onSelectPackage(pkg.id)}
                        className={`w-full rounded-xl px-6 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-1 ${theme.buttonClass}`}
                      >
                        Buy Now
                      </button>
                    ) : (
                      <Link
                        href="/register"
                        className={`inline-block w-full rounded-xl px-6 py-3 text-center font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-1 ${theme.buttonClass}`}
                      >
                        Register Now in 2 minutes
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
